import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { format } from "date-fns";
import { useMemo } from "react";
import {
	ConnectionHandler,
	graphql,
	useLazyLoadQuery,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import { Heading } from "@/components/heading";
import { Card } from "@/components/ui/card";
import { EventForm, type EventFormSchemaValues } from "@/forms/event-form";
import type { eventos_updateQuery } from "./__generated__/eventos_updateQuery.graphql";
import type { eventosUpdateMutation } from "./__generated__/eventosUpdateMutation.graphql";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";

export const Route = createFileRoute(
	"/_visibility/_topnav/eventos/$slug/editar",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { slug: eventSlug } = useParams({ from: Route.id });
	const { club } = useRouteContext({ from: "__root__" });
	const { track } = useAnalytics();

	const data = useLazyLoadQuery<eventos_updateQuery>(
		graphql`query eventos_updateQuery($club: String!, $eventSlug: String!) {
				club(slug: $club) {
				  id
					event(slug: $eventSlug) {
						id
						title
						description
						host {
							id
						}
						coverPhoto {
							id
							signedUrl
						}
						visibility
						startDate
						endDate
						eventUrl
						type
						address
						badges {
							id
						}
						tags {
							id
						}
						externalProducts {
							id
						}
						sendConfirmationEmail
						sendReminderEmail
					}
          offers(first: 10000) {
					  edges {
					    node {
    					  id
    					  productName
					    }
					  }
					}
					admins(first: 10000) {
						edges {
							node {
		            id
		            displayName
		            avatarFallback
		            photo {
		              signedUrl
		            }
							}
						}
					}
					tags(first: 10000) {
					  edges {
						  node {
								id
								text
							}
						}
					}
					badges(first: 10000) {
					  edges {
							node {
					      id
								...memberBadgeFragment
							}
						}
					}
				}
		}`,
		{ club: club.slug, eventSlug },
	);

	const [updateEvent] = useMutation<eventosUpdateMutation>(graphql`
	   mutation eventosUpdateMutation($club: String!, $payload: UpdateEventInput!) {
	       updateEvent(payload: $payload, club: $club) {
	           eventEdge {
	               node {
	                 id
									 slug
   					      ...eventCard
   					      ...eventFragment
   					      ...eventSummaryFragment
	               }
	           }
	       }
	   }
	 `);

	const availableHosts = data.club.admins?.edges?.map((ed) => ed.node);

	const onSubmit = (value: EventFormSchemaValues) => {
		return updateEvent({
			variables: {
				club: club.slug,
				payload: {
					id: data.club.event.id,
					title: value.title,
					description: JSON.stringify(value.description),
					host: value.host,
					visibility: value.visibility,
					startDate: value.startDate,
					endDate: value.endDate,
					eventType: value.type,
					url: value.type === "LIVE" ? value.url : null,
					address: value.type === "IN_PERSON" ? value.address : null,
					badges: value.badges,
					tags: value.tags,
					externalProducts: value.products,
					sendConfirmationEmail: value.sendConfirmationEmail,
					sendReminderEmail: value.sendReminderEmail,
				},
			},
			onCompleted: (response) => {
				track(AnalyticsEvents.EVENT_EDITED, { event_id: data.club.event.id });
				toast.success("Evento atualizado com sucesso!");

				navigate({
					to: "/eventos/$slug",
					params: { slug: response.updateEvent.eventEdge.node.slug },
				});
			},
			onError: () => {
				toast.error(
					"Não foi possível atualizar o evento no momento. Verifique os dados preenchidos e tente novamente.",
				);
			},
			updater: (store) => {
				const clubRecord = store.get(data.club.id);

				if (clubRecord) {
					const filters = ["UPCOMING", "PAST"];
					const orderBys = ["CHRONOLOGICAL", "REVERSE"];

					filters.forEach((f) => {
						orderBys.forEach((o) => {
							const connection = ConnectionHandler.getConnection(
								clubRecord,
								"eventosFeedFragment__eventsByMonth",
								{ orderBy: o, filter: f },
							);

							connection?.invalidateRecord();
						});
					});
				}
			},
		});
	};

	const availableProducts = useMemo(() => {
		return (
			data?.club?.offers?.edges?.flatMap(({ node: product }) => {
				return product;
			}) || []
		);
	}, [data?.club?.offers]);

	const availableBadges = useMemo(() => {
		return data?.club?.badges?.edges?.map((edge) => edge.node) || [];
	}, [data?.club?.badges]);

	const availableTags = useMemo(() => {
		return data?.club?.tags?.edges?.map((edge) => edge.node) || [];
	}, [data?.club?.tags]);

	return (
		<div className="size-full flex items-center justify-center py-4 md:py-8">
			<Card className="p-6 w-full max-md:px-4 max-w-[700px] flex flex-col gap-6">
				<Heading>Editar Evento</Heading>

				<EventForm
					onSubmit={onSubmit}
					products={availableProducts}
					tags={availableTags}
					badges={availableBadges}
					hosts={availableHosts}
					defaultValues={{
						title: data.club.event.title,
						description: data.club.event.description,
						host: data.club.event.host.id,
						cover: data.club.event.coverPhoto
							? {
									id: data.club.event.coverPhoto.id,
									url: data.club.event.coverPhoto.signedUrl,
								}
							: null,
						startDate: format(
							new Date(data.club.event.startDate),
							"yyyy-MM-dd'T'HH:mm",
						).slice(0, 16),
						endDate: format(
							new Date(data.club.event.endDate),
							"yyyy-MM-dd'T'HH:mm",
						).slice(0, 16),
						type:
							data.club.event.type === "%future added value"
								? "LIVE"
								: data.club.event.type,
						visibility:
							data.club.event.visibility === "%future added value"
								? "PUBLIC"
								: data.club.event.visibility,
						url: data.club.event.eventUrl || "",
						address: data.club.event.address || "",
						badges: data.club.event.badges.map((b) => b.id),
						tags: data.club.event.tags.map((t) => t.id),
						products: data.club.event.externalProducts.map((ep) => ep.id),
						sendPromotionEmail: false,
						sendConfirmationEmail: data.club.event.sendConfirmationEmail,
						sendReminderEmail: data.club.event.sendReminderEmail,
					}}
				/>
			</Card>
		</div>
	);
}
